/**
 * Org Connection Audit Panel (S8).
 *
 * RFC §4.9: org admins need a single pane that answers "which
 * integrations have ever touched a run inside this org, and who
 * authorized them?" — without ever exposing token bytes or the
 * broker's connected-account handle. Backed by the
 * `orgConnectionAudit(orgId)` query;
 * the resolver enforces metadata-only projection (ADR-0002 M2),
 * so this component cannot accidentally render a credential
 * surface even if a future schema change tries to add one.
 *
 * Read-gated: any org member can view the audit (the "policing"
 * role for a regular member is intentional). Writes
 * (revoke / connect) live elsewhere and are gated to OWNER/ADMIN
 * by `requireOrgAdmin` server-side.
 */
import React from 'react';
import {useQuery} from '@apollo/client';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {GET_ORG_CONNECTION_AUDIT} from '../../graphql/queries';
import {ConnectionTier, type OrgAuditEntry} from '../../types/generated/graphql';
import {IntegrationLogo} from '../../integrations';

interface QueryResult {
  orgConnectionAudit: OrgAuditEntry[];
}

interface Props {
  orgId: string;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function tierColor(tier: ConnectionTier): 'primary' | 'secondary' {
  return tier === ConnectionTier.Org ? 'primary' : 'secondary';
}

function tierLabel(tier: ConnectionTier): string {
  switch (tier) {
    case ConnectionTier.Org:
      return 'Org-shared';
    case ConnectionTier.User:
      return 'Member fallback';
    case ConnectionTier.Run:
      return 'Run (ephemeral)';
    case ConnectionTier.Platform:
      return 'Platform';
    default:
      return tier;
  }
}

export const OrgConnectionAuditPanel: React.FC<Props> = ({orgId}) => {
  const {data, loading, error} = useQuery<QueryResult>(GET_ORG_CONNECTION_AUDIT, {
    variables: {orgId},
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{my: 2}}>
        Failed to load audit feed: {error.message}
      </Alert>
    );
  }

  const rows = data?.orgConnectionAudit ?? [];

  if (rows.length === 0) {
    return (
      <Alert severity="info" sx={{my: 2}}>
        No connection activity in this org yet. As members consent to
        share personal connections inside org runs, those events show up
        here.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{mt: 1}}>
      <Table size="small" aria-label="Org connection audit">
        <TableHead>
          <TableRow>
            <TableCell>Integration</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell>Authorized by</TableCell>
            <TableCell>First connected</TableCell>
            <TableCell>Last used</TableCell>
            <TableCell>Last run</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={`${row.integrationId}:${row.principalId}:${idx}`}
              hover
            >
              <TableCell>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <IntegrationLogo
                    integrationId={row.integrationId}
                    name={row.integrationId}
                    size={18}
                    variant="mono"
                  />
                  <Typography variant="body2" sx={{fontWeight: 500}}>
                    {row.integrationId}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={tierColor(row.tier)}
                  label={tierLabel(row.tier)}
                  variant={row.tier === ConnectionTier.Org ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>
                <Tooltip title="Internal principal ID — not a personally identifying handle.">
                  <Typography
                    variant="caption"
                    sx={{fontFamily: 'monospace', color: 'text.secondary'}}
                  >
                    {row.principalId}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(row.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(row.lastUsedAt)}
                </Typography>
              </TableCell>
              <TableCell>
                {row.lastUsedByRunId ? (
                  <Typography
                    variant="caption"
                    sx={{fontFamily: 'monospace', color: 'text.secondary'}}
                  >
                    {row.lastUsedByRunId}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
